import React, { useState, useEffect } from 'react';
import { MdClose, MdTrendingUp, MdPerson, MdSave, MdRefresh } from 'react-icons/md';
import axios from 'axios';

const SetTargetsModal = ({ isOpen, onClose, officers, userMmdaId, user }) => {
  const [targets, setTargets] = useState([]);
  const [targetType, setTargetType] = useState('monthly');
  const [targetPeriod, setTargetPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const config = {
    headers: {
      'Authorization': 'Basic ' + btoa('Techland:Techland@24'),
      'Content-Type': 'application/json'
    }
  };

  useEffect(() => {
    if (isOpen && officers) {
      initializeTargets();
      setDefaultPeriod();
    }
  }, [isOpen, officers, targetType]);

  const setDefaultPeriod = () => {
    const now = new Date();
    if (targetType === 'monthly') {
      setTargetPeriod(now.toISOString().slice(0, 7)); // YYYY-MM
    } else if (targetType === 'quarterly') {
      const quarter = Math.ceil((now.getMonth() + 1) / 3);
      setTargetPeriod(`${now.getFullYear()}-Q${quarter}`);
    } else if (targetType === 'yearly') {
      setTargetPeriod(now.getFullYear().toString());
    }
  };

  const initializeTargets = () => {
    const initialTargets = officers.map(officer => ({
      officer_id: officer.officer_id,
      officer_name: `${officer.first_name} ${officer.last_name}`,
      zone_name: officer.zone_name,
      target_amount: '',
      target_description: ''
    }));
    setTargets(initialTargets);
  };

  const handleTargetChange = (officerId, field, value) => {
    setTargets(prev => prev.map(target => 
      target.officer_id === officerId 
        ? { ...target, [field]: value }
        : target
    ));
  };

  const handleBulkAmount = (amount) => {
    setTargets(prev => prev.map(target => ({
      ...target,
      target_amount: amount
    })));
  };

  const handleSaveTargets = async () => {
    setLoading(true);
    try {
      const targetsToSave = targets
        .filter(target => target.target_amount && parseFloat(target.target_amount) > 0)
        .map(target => ({
          officer_id: target.officer_id,
          target_type: targetType,
          target_amount: parseFloat(target.target_amount),
          target_period: targetPeriod,
          target_description: target.target_description || `${targetType} target for ${targetPeriod}`,
      created_by: user?.user_id
        }));

      if (targetsToSave.length === 0) {
        setShowPopup({ message: 'Please set at least one target amount', type: 'error' });
        setLoading(false);
        return;
      }

      await axios.post(`${API_BASE_URL}/officer-performance/targets/bulk`, {
        targets: targetsToSave
      }, config);

      setShowPopup({ message: `Successfully set targets for ${targetsToSave.length} officers`, type: 'success' });
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error saving targets:', error);
      setShowPopup({ message: 'Failed to save targets', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 32,
        width: '90%',
        maxWidth: 800,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '2px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdTrendingUp size={28} style={{ color: '#1976d2' }} />
            <div>
              <h2 style={{ margin: 0, color: '#1976d2', fontSize: 22, fontWeight: 'bold' }}>
                Set Performance Targets
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                Set targets for {officers.length} revenue officers
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Target Configuration */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16 }}>Target Configuration</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#333', fontSize: 14 }}>
                Target Type
              </label>
              <select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#333', fontSize: 14 }}>
                Target Period
              </label>
              <input
                type="text"
                value={targetPeriod}
                onChange={(e) => setTargetPeriod(e.target.value)}
                style={{
                  width: '95%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  
                }}
                placeholder="e.g., 2024-01, 2024-Q1, 2024"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#333', fontSize: 14 }}>
                Bulk Amount (GH₵)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  placeholder="Set for all"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleBulkAmount(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = e.target.previousElementSibling;
                    handleBulkAmount(input.value);
                    input.value = '';
                  }}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Officers List */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16 }}>Individual Targets</h3>
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontSize: 14 }}>
                    Officer
                  </th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontSize: 14 }}>
                    Zone
                  </th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontSize: 14 }}>
                    Target Amount (GH₵)
                  </th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontSize: 14 }}>
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target, index) => (
                  <tr key={target.officer_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 12, fontSize: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdPerson size={16} style={{ color: '#666' }} />
                        {target.officer_name}
                      </div>
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#666' }}>
                      {target.zone_name}
                    </td>
                    <td style={{ padding: 12 }}>
                      <input
                        type="number"
                        value={target.target_amount}
                        onChange={(e) => handleTargetChange(target.officer_id, 'target_amount', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          fontSize: 14,
                        }}
                        placeholder="0.00"
                      />
                    </td>
                    <td style={{ padding: 12 }}>
                      <input
                        type="text"
                        value={target.target_description}
                        onChange={(e) => handleTargetChange(target.officer_id, 'target_description', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          fontSize: 14,
                        }}
                        placeholder="Optional description"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              border: '1px solid #6c757d',
              borderRadius: 6,
              backgroundColor: 'white',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveTargets}
            disabled={loading}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: 6,
              backgroundColor: loading ? '#ccc' : '#1976d2',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <MdSave size={16} />
            {loading ? 'Saving...' : 'Save Targets'}
          </button>
        </div>

        {/* Popup Notification */}
        {showPopup && (
          <div style={{
            position: 'fixed',
            top: 20,
            right: 20,
            backgroundColor: showPopup.type === 'success' ? '#d4edda' : '#f8d7da',
            color: showPopup.type === 'success' ? '#155724' : '#721c24',
            padding: 12,
            borderRadius: 4,
            border: `1px solid ${showPopup.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            zIndex: 1001,
            maxWidth: 300,
          }}>
            {showPopup.message}
            <button
              onClick={() => setShowPopup(null)}
              style={{
                marginLeft: 10,
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetTargetsModal;

